import type { AssetType, EngineConfig, Kind, OpName, Pillar } from './types';

/** Pillars an asset type may be filed under; the first one is the suggested default. */
export const ALLOWED: Record<AssetType, Pillar[]> = {
  'Acción': ['Bolsa', 'Materias primas', 'Criptomonedas'], 'ETF': ['Bolsa', 'Materias primas', 'Criptomonedas'],
  'Fondo': ['Bolsa', 'Materias primas', 'Criptomonedas'], 'ETC': ['Materias primas', 'Bolsa', 'Criptomonedas'],
  'Materia prima': ['Materias primas'], 'Criptomoneda': ['Criptomonedas'],
};

export const ALL_PILLARS: Pillar[] = ['Bolsa', 'Materias primas', 'Criptomonedas'];
/** Pillars an asset of `type` may be filed under: the prototype's ALLOWED, or all of them (its own first) with allPillars. */
export function pillarsFor(config: Pick<EngineConfig, 'allPillars'>, type: AssetType): Pillar[] {
  const own = ALLOWED[type];
  return config.allPillars ? [...own, ...ALL_PILLARS.filter(p => !own.includes(p))] : own;
}

export interface OpDef { t: string; kind: Kind; amt?: boolean; sched?: boolean; btn: string; grp: 'open' | 'close' | 'sched' }
export const OPS: Record<OpName, OpDef> = {
  buy: { t: 'Comprar', kind: 'buy', amt: true, btn: 'Confirmar compra', grp: 'open' },
  buyMore: { t: 'Comprar más', kind: 'buy', amt: true, btn: 'Confirmar compra', grp: 'open' },
  schedBuy: { t: 'Compra programada', kind: 'buy', amt: true, sched: true, btn: 'Programar compra', grp: 'sched' },
  short: { t: 'Vender en corto', kind: 'short', amt: true, btn: 'Confirmar corto', grp: 'open' },
  addShort: { t: 'Ampliar corto', kind: 'short', amt: true, btn: 'Confirmar corto', grp: 'open' },
  sell: { t: 'Vender', kind: 'sell', btn: 'Confirmar venta', grp: 'close' },
  schedSell: { t: 'Venta programada', kind: 'sell', sched: true, btn: 'Programar venta', grp: 'sched' },
  cover: { t: 'Recomprar (cerrar corto)', kind: 'cover', btn: 'Confirmar recompra', grp: 'close' },
  schedCover: { t: 'Recompra programada', kind: 'cover', sched: true, btn: 'Programar recompra', grp: 'sched' },
};

/** Prototype fees (€ per executed order). The SRS says "no commissions": pass ZERO_FEES once the client confirms. */
export const PROTOTYPE_FEES: Record<Kind, number> = { buy: 1, sell: 1, short: 3, cover: 0 };
export const ZERO_FEES: Record<Kind, number> = { buy: 0, sell: 0, short: 0, cover: 0 };

/**
 * Units within this of a position count as all of it. The order form fills "Vender todo" / "Recomprar todo" with the free
 * units rounded to 8 decimals: rounded down it must still close the position, rounded up it must not be refused.
 */
export const UNIT_EPS = 1e-8;

export const DEFAULT_CONFIG: EngineConfig = { start: '2026-11-01', end: '2027-10-31', initial: 10000, fees: PROTOTYPE_FEES };
